import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  TIKTOK_SHOP_AUTH_BROADCAST_CHANNEL,
  TIKTOK_SHOP_AUTH_RESPONSE_KEY,
  TIKTOK_SHOP_AUTH_RESULT,
  TiktokShopAuthState,
  type TiktokShopAuthResultMessage,
} from "@type/tiktokShopAuth";

const parseState = (raw: string | null): TiktokShopAuthState | null => {
  if (raw === TiktokShopAuthState.Ok) return TiktokShopAuthState.Ok;
  if (raw === TiktokShopAuthState.Error) return TiktokShopAuthState.Error;
  return null;
};

export const PageTiktokShopAuth = () => {
  const [searchParams] = useSearchParams();
  const state = parseState(searchParams.get("state"));
  const msg = searchParams.get("msg") ?? undefined;

  useEffect(() => {
    try {
      window.localStorage.setItem(
        TIKTOK_SHOP_AUTH_RESPONSE_KEY,
        JSON.stringify({
          state,
          msg,
        }),
      );
    } catch {
      // do nothing, it's ok to ignore error
    }
    const payload: TiktokShopAuthResultMessage = { type: TIKTOK_SHOP_AUTH_RESULT, state, msg };

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(TIKTOK_SHOP_AUTH_BROADCAST_CHANNEL);
      // 主通道：BroadcastChannel 不依赖 window.opener，COOP 隔离下仍可达
      // 用 bind 绕开 oxc postmessage-target-origin 规则对 BroadcastChannel.postMessage 的误报
      const post: (msg: unknown) => void = channel.postMessage.bind(channel);
      post(payload);
    } catch {
      // BroadcastChannel 不可用时退化为下方 opener 兜底
    }

    // 备用通道：opener 还连得上时也发一次，省一道延迟（COOP 切断时 opener 为 null，跳过即可）
    const opener = window.opener as Window | null;
    if (opener && !opener.closed) {
      try {
        opener.postMessage(payload, window.location.origin);
      } catch {
        // opener 跨上下文不可达时静默忽略
      }
    }

    // 弹窗对自己 window 的 close 通常被允许（即便 opener 已断），尝试自闭
    try {
      window.close();
    } catch {
      /* noop */
    }

    return () => {
      try {
        channel?.close();
      } catch {
        /* noop */
      }
    };
  }, [state, msg]);

  return null;
};
